import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import "./Header.css";
import Preheader from "./Preheader";
import { FaHeart, FaShoppingBag, FaUser } from "react-icons/fa";
import Swal from "sweetalert2";

function Header() {
  const navigate = useNavigate();

  const [allCategories, setAllCategories] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);

  const [email, setEmail] = useState(null);
  const [userName, setUserName] = useState(null);

  const [cartCount, setCartCount] = useState(0);
  const [wishlistCount, setWishlistCount] = useState(0);

  const [products, setProducts] = useState([]);

  // ----------------------------------------------------
  // LOAD USER SESSION DATA ONCE
  // ----------------------------------------------------
  useEffect(() => {
    const user = JSON.parse(sessionStorage.getItem("user"));
    if (user?.email) {
      setEmail(user.email);
      setUserName(user.fullname || user.email.split("@")[0]);
    }
  }, []);

  // ----------------------------------------------------
  // FETCH PRODUCTS ONCE
  // ----------------------------------------------------
  useEffect(() => {
    axios
      .get(
        "https://api.dremerz.net/api/DZ_8925_KalanjiyamEcom_Products_AllProducts_ProductsList"
      )
      .then((res) => setProducts(res.data || []))
      .catch(() => setProducts([]));
  }, []);

  // ----------------------------------------------------
  // FETCH CATEGORIES ONCE
  // ----------------------------------------------------
  useEffect(() => {
    axios
      .get(
        "https://api.dremerz.net/api/DZ_8925_KalanjiyamEcom_Products_Category_CategoryList"
      )
      .then((res) => setAllCategories(res.data || []))
      .catch(() => setAllCategories([]));
  }, []);

  // ----------------------------------------------------
  // CLICK OUTSIDE DROPDOWN HANDLER
  // ----------------------------------------------------
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest(".header-profile-dropdown")) {
        setShowDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // ----------------------------------------------------
  // UPDATE CART & WISHLIST COUNTS WHEN SESSION CHANGES
  // ----------------------------------------------------
  useEffect(() => {
    const updateCounts = () => {
      if (!products.length) return;

      const cartData = sessionStorage.getItem("userCart");
      const wishlistData = sessionStorage.getItem("userWishlist");

      // CART COUNT
      try {
        if (cartData) {
          const cart = JSON.parse(cartData);
          const validIds = new Set(products.map((p) => p.id.toString()));
          let total = 0;

          Object.values(cart).forEach((item) => {
            if (validIds.has(item.productId?.toString())) total++;
          });

          setCartCount(total);
        } else {
          setCartCount(0);
        }
      } catch {
        setCartCount(0);
      }

      // WISHLIST COUNT
      try {
        if (wishlistData) {
          const wishlist = JSON.parse(wishlistData);
          const valid = wishlist.filter((id) =>
            products.some((p) => p.id.toString() === id.toString())
          );
          setWishlistCount(valid.length);
        } else {
          setWishlistCount(0);
        }
      } catch {
        setWishlistCount(0);
      }
    };

    updateCounts();
    window.addEventListener("session-storage-change", updateCounts);

    return () =>
      window.removeEventListener("session-storage-change", updateCounts);
  }, [products]);

  // ----------------------------------------------------
  // LOGOUT HANDLER
  // ----------------------------------------------------
  const handleLogout = async (e) => {
    e.preventDefault();

    const confirmLogout = await Swal.fire({
      title: "Are you sure you want to logout?",
      icon: "warning",
      showCancelButton: true,
      customClass: {
        title: "swal-small-title",
        popup: "swal-small-popup",
        confirmButton: "swal-small-btn",
        cancelButton: "swal-small-btn",
      },
      heightAuto: false,
    }).then((r) => r.isConfirmed);

    if (confirmLogout) {
      sessionStorage.clear();
      window.dispatchEvent(new Event("session-storage-change"));
      navigate("/login");
    }
  };

  return (
    <>
      {/* ---------------- PREHEADER ---------------- */}
      <Preheader
        email={email}
        userName={userName}
        cartCount={cartCount}
        wishlistCount={wishlistCount}
        products={products}
        onLogout={handleLogout}
      />

      {/* ---------------- MAIN HEADER NAV ---------------- */}
      <div className="nav-container">
        <div className="container categories-container">
          <div className="categories-scroll">
            <Link to="/" className="nav-link text-white">Home</Link>
            {/* <Link to="/4" className="nav-link text-white">Shop</Link>
            <Link to="/3" className="nav-link text-white">Sale</Link>
            <Link to="/7" className="nav-link text-white">Discount</Link> */}
            {allCategories.length > 0 &&
              allCategories.map((cat) => (
                <Link
                  key={cat.id}
                  to={`/${cat.id}`}
                  className="nav-link text-white"
                >
                  {cat.CategoryName}
                </Link>
              ))}
          </div>
        </div>

        {/* ---------------- MOBILE BOTTOM NAV ---------------- */}
        <div className="w-100 nav-bottom d-flex d-md-none">

          <Link to="/" className="nav-link text-white nav-icon">
            <i className="fas fa-home fa-lg"></i>
          </Link>

          <Link to="/MyProfile/Addtocart" className="nav-link text-white nav-icon">
            <div className="icon-with-badge">
              <FaShoppingBag size={20} />
              {cartCount > 0 && (
                <span className="count-badge">
                  {cartCount > 99 ? "99+" : cartCount}
                </span>
              )}
            </div>
          </Link>

          <Link to="/MyProfile/Wishlist" className="nav-link text-white nav-icon">
            <div className="icon-with-badge">
              <FaHeart size={20} />
              {wishlistCount > 0 && (
                <span className="count-badge">
                  {wishlistCount > 99 ? "99+" : wishlistCount}
                </span>
              )}
            </div>
          </Link>

          <Link to="/TrackYourOrder/myOrders" className="nav-link text-white nav-icon">
            <i className="fas fa-truck fa-lg"></i>
          </Link>

          {/* PROFILE ICON */}
          {email ? (
            <div className="header-profile-dropdown nav-icon">
              <Link
                to="#"
                className="nav-link text-white"
                onClick={(e) => {
                  e.preventDefault();
                  setShowDropdown(!showDropdown);
                }}
              >
                <FaUser size={20} />
              </Link>

              {showDropdown && (
                <div className="header-dropdown-menu">
                  <button
                    onClick={() => navigate("/myProfile")}
                    className="header-dropdown-item"
                  >
                    My Profile
                  </button>
                  <button
                    className="header-dropdown-item header-dropdown-logout"
                    onClick={handleLogout}
                  >
                    Log Out
                  </button>
                </div>
              )}
            </div>
          ) : (
            <Link to="/login" className="nav-link text-white nav-icon">
              <FaUser size={20} />
            </Link>
          )}
        </div>
      </div>
    </>
  );
}

export default Header;
